// Manual del Libro de caja (Caja Repuestos). Se muestra en la pantalla Ayuda y tambien en
// /ayuda/imprimir, que es la version sin menu de donde se genera el PDF descargable
// (public/ayuda/Manual_Libro_de_Caja.pdf). Las imagenes estan en public/ayuda/.
const Paso = ({ n, children }: { n: number; children: React.ReactNode }) => (
  <li className="flex gap-3 items-start">
    <span className="shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">{n}</span>
    <div className="pt-0.5">{children}</div>
  </li>
);

const Imagen = ({ src, alt, ancho = 'max-w-md' }: { src: string; alt: string; ancho?: string }) => (
  <img src={`/ayuda/${src}`} alt={alt} className={`${ancho} w-full border rounded-lg shadow-sm my-3 manual-imagen`} />
);

const Seccion = ({ titulo, children }: { titulo: string; children: React.ReactNode }) => (
  <section className="mb-8 manual-seccion">
    <h2 className="text-xl font-bold text-blue-800 border-b-2 border-blue-200 pb-1 mb-3">{titulo}</h2>
    <div className="space-y-3 text-gray-800 leading-relaxed">{children}</div>
  </section>
);

const Aviso = ({ children, color = 'amber' }: { children: React.ReactNode; color?: 'amber' | 'red' | 'green' }) => {
  const c = { amber: 'bg-amber-50 border-amber-400', red: 'bg-red-50 border-red-400', green: 'bg-green-50 border-green-500' }[color];
  return <div className={`border-l-4 ${c} px-4 py-2 rounded`}>{children}</div>;
};

const ManualLibroCaja = () => (
  <div className="manual">
    <div className="mb-8">
      <h1 className="text-3xl font-bold text-gray-900">Manual del Libro de caja</h1>
      <p className="text-gray-600">Caja Repuestos · Sistema Rápido · Actualizado el 20/10/2026</p>
    </div>

    <Seccion titulo="1. ¿Qué es el Libro de caja?">
      <p>
        Es la Caja Repuestos con el mismo formato del Excel <b>"REPUESTO"</b>: una lista día por día con
        <b> Fecha, Descripción, Entrada, Salida y Saldo</b>. El saldo se calcula solo, fila por fila.
      </p>
      <p>La diferencia con el Excel es que aquí todo queda conectado:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Una <b>venta</b> descuenta el producto del <b>stock</b>.</li>
        <li>Una <b>compra</b> puede sumar el producto al stock.</li>
        <li>Un <b>pago de deuda</b> baja la deuda del dueño del bus.</li>
        <li>El saldo del libro siempre es igual al saldo real de la caja.</li>
      </ul>
      <Aviso color="green">
        <b>Desde que empiecen a usarlo, anoten todo aquí y dejen el Excel.</b> Si se anota en los dos lados, el stock y
        el saldo dejan de cuadrar.
      </Aviso>
    </Seccion>

    <Seccion titulo="2. Cómo entrar">
      <ol className="space-y-2">
        <Paso n={1}>En el menú de la izquierda, toca <b>Caja Repuestos</b>.</Paso>
        <Paso n={2}>La pantalla abre directo en la pestaña <b>"Libro de caja"</b>. La otra pestaña, "Historial de ingresos", es la vista antigua.</Paso>
      </ol>
    </Seccion>

    <Seccion titulo="3. Ver el libro">
      <Imagen src="01_libro.png" alt="Libro de caja" ancho="max-w-3xl" />
      <ul className="list-disc pl-6 space-y-1">
        <li><b>Hoy / Semana / Mes</b>: eligen rápido qué días ver. También se puede poner <b>Desde</b> y <b>Hasta</b> y tocar <b>Ver</b>.</li>
        <li>La primera fila es el <b>SALDO ANTERIOR</b> (lo que había en la caja antes del primer día elegido).</li>
        <li>Las <b>entradas</b> suman y las <b>salidas</b> (en amarillo) restan. La última fila tiene los <b>TOTALES</b> y el saldo final.</li>
        <li>Si un pago fue por <b>Yape</b>, aparece la etiqueta morada YAPE.</li>
        <li>Los botones <b>Excel</b> y <b>PDF</b> descargan exactamente lo que se está viendo.</li>
      </ul>
    </Seccion>

    <Seccion titulo="4. Anotar una VENTA (entrada)">
      <p>Para todo lo que se vende y se paga en el momento.</p>
      <ol className="space-y-2">
        <Paso n={1}>Toca el botón azul <b>"+ Agregar fila"</b>. Queda marcada la opción <b>Venta</b>.</Paso>
        <Paso n={2}>En el buscador escribe parte del nombre o el código del producto (no importan las tildes ni las mayúsculas) y tócalo en la lista. Ahí se ve el stock que hay.</Paso>
      </ol>
      <Imagen src="02_buscar.png" alt="Buscar producto" />
      <ol className="space-y-2" start={3}>
        <Paso n={3}>Pon la <b>cantidad</b>. El <b>precio</b> sale solo; si se cobró otro precio, cámbialo.</Paso>
        <Paso n={4}>Si se venden varios productos juntos, búscalos y agrégalos uno por uno. Con el tacho rojo se quita un producto.</Paso>
        <Paso n={5}>Opcional: el <b>bus</b> (padrón) o <b>a nombre de</b> quién es la venta (por ejemplo "Pacheco"). Si pones el bus, sale el nombre del dueño.</Paso>
        <Paso n={6}>Elige <b>Efectivo</b> o <b>Yape / Plin</b> y toca <b>"Guardar fila"</b>.</Paso>
      </ol>
      <Imagen src="03_venta.png" alt="Venta lista para guardar" />
      <Aviso>
        Si la cantidad es mayor que el stock del sistema, no deja guardar. En ese caso hay que corregir el stock en la
        pantalla <b>Stock</b> (o registrar primero la compra, ver punto 6).
      </Aviso>
      <Aviso color="red">
        Aquí solo van ventas <b>al contado</b>. Las ventas <b>a crédito</b> se hacen en <b>Ventas</b>, porque no entra plata a la caja.
      </Aviso>
    </Seccion>

    <Seccion titulo="5. Anotar un GASTO (salida)">
      <p>Para pagos sueltos que salen de la caja: taxi, movilidad, un pago a una persona, etc.</p>
      <ol className="space-y-2">
        <Paso n={1}>Toca <b>"+ Agregar fila"</b> y luego <b>Gasto</b>.</Paso>
        <Paso n={2}>Escribe en qué se gastó y el monto.</Paso>
        <Paso n={3}>Toca <b>"Guardar fila"</b>. Aparece en el libro como salida.</Paso>
      </ol>
      <Imagen src="04_gasto.png" alt="Gasto" />
    </Seccion>

    <Seccion titulo="6. Anotar una COMPRA DE REPUESTOS (salida)">
      <ol className="space-y-2">
        <Paso n={1}>Toca <b>"+ Agregar fila"</b> y luego <b>Compra de repuestos</b>.</Paso>
        <Paso n={2}>Si lo comprado es un producto que se vende, búscalo arriba: así la cantidad <b>se suma al stock</b>.</Paso>
        <Paso n={3}>Escribe qué se compró, la cantidad, el <b>monto total</b> pagado y el proveedor (opcional).</Paso>
        <Paso n={4}>Toca <b>"Guardar fila"</b>.</Paso>
      </ol>
      <Imagen src="05_compra.png" alt="Compra de repuestos" />
    </Seccion>

    <Seccion titulo="7. PAGO DE DEUDA de un dueño (entrada)">
      <p>
        Cuando un dueño paga lo que debe (por ejemplo "SRA OLGA 3733.50"), se registra en <b>Créditos</b>. Ahí se elige
        al dueño y el pago se reparte entre sus buses. La parte que es <b>servicio</b> (mano de obra) pasa sola a Caja
        Servicios, así que <b>no hay que sacarla a mano</b> como en el Excel ("SERVICIO OLGA 340").
      </p>
      <ol className="space-y-2">
        <Paso n={1}>Toca <b>"+ Agregar fila"</b>, luego <b>Pago de deuda</b> y <b>"Ir a Cobrar deuda"</b>.</Paso>
        <Paso n={2}>Al terminar el cobro, el pago aparece en el libro como entrada.</Paso>
      </ol>
      <Imagen src="06_deuda.png" alt="Pago de deuda" />
    </Seccion>

    <Seccion titulo="8. Corregir un error: ANULAR una fila">
      <p>Las ventas, gastos, compras y pagos de deuda tienen al final de la fila el botón <b>⊘</b>.</p>
      <ol className="space-y-2">
        <Paso n={1}>Toca <b>⊘</b> en la fila equivocada.</Paso>
        <Paso n={2}>Escribe la <b>clave de administrador</b> (pídesela al encargado) y el <b>motivo</b> (por ejemplo "se anotó dos veces").</Paso>
        <Paso n={3}>Toca <b>Anular</b>.</Paso>
      </ol>
      <Imagen src="07_anular.png" alt="Anular fila" />
      <ul className="list-disc pl-6 space-y-1">
        <li><b>Venta anulada:</b> el producto vuelve al stock y la plata sale de la caja. La venta queda marcada "(ANULADA)" y aparece la salida "ANULACIÓN DE VENTA".</li>
        <li><b>Gasto anulado:</b> la plata vuelve a la caja.</li>
        <li><b>Compra anulada:</b> la plata vuelve a la caja y, si se había sumado al stock, se resta.</li>
        <li>Todo queda registrado en <b>Auditoría</b>, en rojo.</li>
        <li><b>Pago de deuda anulado:</b> la plata sale de la caja y la deuda del cliente vuelve a subir. Si el cliente pagó otro monto, regístralo después en <b>Créditos</b> con el monto correcto. También se puede anular desde <b>Créditos</b> → cliente → "Pagos de deuda antigua".</li>
        <li><b>Ojo:</b> para corregir un pago mal anotado NO se agrega deuda nueva; se anula el pago.</li>
      </ul>
    </Seccion>

    <Seccion titulo="9. Preguntas frecuentes">
      <p><b>¿Quiénes pueden anotar?</b> Cualquiera que use el sistema (Rogelio, Milenko). Para anular se necesita la clave.</p>
      <p><b>¿Puedo anotar una venta de ayer?</b> Las filas nuevas quedan con la fecha y hora del momento en que se guardan. Lo ideal es anotar cada venta al momento.</p>
      <p><b>El stock del sistema no coincide con lo que hay en el estante.</b> Mientras se usaba el Excel, las ventas no se descontaban del sistema. Antes de empezar, cuenten lo que más se vende y corrijan la cantidad en <b>Stock</b>.</p>
      <p><b>¿Por qué el saldo anterior es muy alto si elijo fechas antes del 10/10/2026?</b> Ese día la caja empezó de nuevo con S/ 2,415.50 (saldo inicial). Las fechas desde el 10/10 en adelante son las que valen.</p>
      <p><b>No aparece algo que se cobró como servicio.</b> La mano de obra entra a <b>Caja Servicios</b>, no a Caja Repuestos.</p>
    </Seccion>
  </div>
);

export default ManualLibroCaja;
